// shared/common/datetimeUtils.js
//
// Toutes les dates/heures sont stockées en base en UTC+0 (datetime.utcnow côté
// backend) et sérialisées en ISO SANS suffixe "Z" (ex: "2026-06-12T13:49:12").
// JavaScript interprète ces chaînes "naïves" comme heure LOCALE, ce qui affiche
// l'heure UTC sans conversion. Ces helpers parsent systématiquement les
// timestamps comme UTC puis formatent dans la timezone de l'ordinateur.
//
// Règles :
// - Timestamp (date + heure)  -> converti UTC -> timezone locale.
// - Date seule ("2026-06-12") -> affichée telle quelle, JAMAIS décalée
//   (sinon le jour peut changer selon la timezone).

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const FR_DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;
// ISO naïf: pas de "Z" ni d'offset (+02:00 / -05:00) à la fin
const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

export const isDateOnlyString = (value) =>
  typeof value === "string" && (DATE_ONLY_RE.test(value.trim()) || FR_DATE_RE.test(value.trim()));

/**
 * Parse une valeur datetime venant de l'API en instant correct.
 * - Chaîne ISO naïve (sans Z/offset) => interprétée comme UTC.
 * - Chaîne avec Z/offset, Date, epoch => passées telles quelles.
 * - Date seule ("YYYY-MM-DD" / "DD/MM/YYYY") => midi local (pas de décalage de jour).
 * Retourne null si invalide.
 */
export const parseUtc = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== "string") return null;

  const raw = value.trim();
  if (!raw) return null;

  if (DATE_ONLY_RE.test(raw)) {
    const d = new Date(`${raw}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (FR_DATE_RE.test(raw)) {
    const [day, month, year] = raw.split("/");
    const d = new Date(`${year}-${month}-${day}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (NAIVE_ISO_RE.test(raw)) {
    const d = new Date(`${raw.replace(" ", "T")}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const resolveLocale = (locale) => locale || undefined; // undefined => locale du navigateur

/** "12/06/2026" — date locale (jour correct même si le timestamp UTC est proche de minuit). */
export const formatLocalDate = (value, { locale, ...options } = {}) => {
  const d = parseUtc(value);
  if (!d) return "";
  return d.toLocaleDateString(resolveLocale(locale), options);
};

/** "15:49" — heure locale. */
export const formatLocalTime = (value, { locale, ...options } = {}) => {
  const d = parseUtc(value);
  if (!d) return "";
  return d.toLocaleTimeString(resolveLocale(locale), {
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
};

/** "12/06/2026 15:49" — date + heure locales. */
export const formatLocalDateTime = (value, { locale, ...options } = {}) => {
  const d = parseUtc(value);
  if (!d) return "";
  return d.toLocaleString(resolveLocale(locale), {
    dateStyle: options.dateStyle,
    timeStyle: options.timeStyle,
    ...((options.dateStyle || options.timeStyle)
      ? {}
      : {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    ...options,
  });
};

/** Valeur pour <input type="datetime-local"> (timezone locale) depuis un timestamp UTC. */
export const toDatetimeLocalInputValue = (value) => {
  const d = parseUtc(value);
  if (!d) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

/** Convertit une valeur <input type="datetime-local"> (locale) en ISO UTC pour la base. */
export const datetimeLocalInputToUtcIso = (value) => {
  if (!value) return null;
  const d = new Date(value); // interprété en heure locale
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
